import { hexToRgb } from "./palettes";

export function heightToColor(h, layers, mode, allSolid) {
  if (!layers.length) return { r: 0, g: 0, b: 0, a: 0 };

  const enriched = layers.map((layer) => ({
    ...layer,
    _rgb: hexToRgb(layer.color),
    _mid: (layer.min + layer.max) / 2,
  }));
  const effectiveSolid = (layer) => allSolid || layer.solid || mode === "exact";

  if (mode === "exact") {
    const inside = enriched.find((layer) => h >= layer.min && h <= layer.max);
    return inside ? { ...inside._rgb, a: 255 } : { r: 0, g: 0, b: 0, a: 0 };
  }

  const insideSolid = enriched.find(
    (layer) => effectiveSolid(layer) && h >= layer.min && h <= layer.max,
  );
  if (insideSolid) return { ...insideSolid._rgb, a: 255 };

  const anchors = enriched
    .filter((layer) => !effectiveSolid(layer))
    .sort((a, b) => a._mid - b._mid);
  if (!anchors.length) {
    const inside = enriched.find((layer) => h >= layer.min && h <= layer.max);
    return inside ? { ...inside._rgb, a: 255 } : { r: 0, g: 0, b: 0, a: 0 };
  }

  let left = null;
  let right = null;
  for (const anchor of anchors) {
    if (anchor._mid <= h) left = anchor;
    if (anchor._mid >= h && right === null) right = anchor;
  }
  if (!left) return { ...anchors[0]._rgb, a: 255 };
  if (!right) return { ...anchors[anchors.length - 1]._rgb, a: 255 };
  if (left === right) return { ...left._rgb, a: 255 };

  const t = (h - left._mid) / (right._mid - left._mid || 1);
  return {
    r: Math.round(left._rgb.r + (right._rgb.r - left._rgb.r) * t),
    g: Math.round(left._rgb.g + (right._rgb.g - left._rgb.g) * t),
    b: Math.round(left._rgb.b + (right._rgb.b - left._rgb.b) * t),
    a: 255,
  };
}

export function buildLUT(layers, mode, allSolid) {
  const lut = new Uint8ClampedArray(256 * 4);
  for (let h = 0; h < 256; h++) {
    const { r, g, b, a } = heightToColor(h, layers, mode, allSolid);
    lut[h * 4] = r;
    lut[h * 4 + 1] = g;
    lut[h * 4 + 2] = b;
    lut[h * 4 + 3] = a;
  }
  return lut;
}

// A height belongs to the first matching layer. Combined-material rendering
// follows this exact rule, so it cannot overwrite a higher-priority layer.
export function buildLayerIndexLUT(layers) {
  const owners = new Int16Array(256);
  owners.fill(-1);
  for (let h = 0; h < 256; h++) {
    for (let index = 0; index < layers.length; index++) {
      const layer = layers[index];
      if (h >= layer.min && h <= layer.max) {
        owners[h] = index;
        break;
      }
    }
  }
  return owners;
}

export function getCombinedMaterialDescriptors(layers) {
  return layers
    .map((layer, ownerLayerIndex) => {
      if (!layer.combine) return null;
      return {
        ...layer.combine,
        min: layer.min,
        max: layer.max,
        ownerLayerIndex,
      };
    })
    .filter(Boolean);
}

export function computeHistogram(sourceImageData) {
  const hist = new Uint32Array(256);
  if (!sourceImageData) return hist;
  const data = sourceImageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const h = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) | 0;
    hist[h]++;
  }
  return hist;
}

export function computeCoverage(layers, histogram) {
  const total = histogram.reduce((sum, value) => sum + value, 0);
  const counts = layers.map(() => 0);
  if (!total) return counts;
  for (let h = 0; h < 256; h++) {
    if (!histogram[h]) continue;
    const owner = layers.findIndex((layer) => h >= layer.min && h <= layer.max);
    if (owner >= 0) counts[owner] += histogram[h];
  }
  return counts.map((count) => (count / total) * 100);
}

export function computeLayerPercentages(layers) {
  const owned = layers.map(() => 0);
  for (let h = 0; h < 256; h++) {
    const owner = layers.findIndex((layer) => h >= layer.min && h <= layer.max);
    if (owner >= 0) owned[owner]++;
  }
  return owned.map((count) => (count / 256) * 100);
}

export function renderColorMap(
  sourceImageData,
  lut,
  combines = [],
  ownerLayerIndices = null,
) {
  return renderWithCombine(sourceImageData, lut, {
    combines,
    ownerLayerIndices,
  });
}

// A height map does not include real-world horizontal/vertical scale, so these
// are estimated image-space angles. The scale keeps common terrain maps useful
// across the full 0-90 degree control while preserving the relative steepness
// of every pixel.
const SLOPE_HEIGHT_SCALE = 16;

export function computeSlopeAngles(sourceImageData) {
  if (!sourceImageData) return null;
  const { width, height, data } = sourceImageData;
  const angles = new Float32Array(width * height);

  const sample = (x, y) => {
    const clampedX = Math.max(0, Math.min(width - 1, x));
    const clampedY = Math.max(0, Math.min(height - 1, y));
    const offset = (clampedY * width + clampedX) * 4;
    return (
      0.2126 * data[offset] +
      0.7152 * data[offset + 1] +
      0.0722 * data[offset + 2]
    );
  };

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const offset = (y * width + x) * 4;
      const index = y * width + x;
      if (data[offset + 3] === 0) {
        angles[index] = -1;
        continue;
      }
      const dx = (sample(x + 1, y) - sample(x - 1, y)) / (2 * 255);
      const dy = (sample(x, y + 1) - sample(x, y - 1)) / (2 * 255);
      const gradient = Math.hypot(dx, dy) * SLOPE_HEIGHT_SCALE;
      angles[index] = Math.atan(gradient) * (180 / Math.PI);
    }
  }
  return angles;
}

export function slopeMatches(angle, threshold, comparison = "gte") {
  if (angle < 0) return false;
  return comparison === "lte" ? angle <= threshold : angle >= threshold;
}

function slopePixelIsProtected(
  sourceImageData,
  pixelIndex,
  ownerLayerIndices,
  protectedOwnerLayerIndex,
  protectedOwnerLayerIndices,
) {
  if (
    !sourceImageData ||
    !ownerLayerIndices ||
    (
      (protectedOwnerLayerIndex === null ||
        protectedOwnerLayerIndex === undefined ||
        protectedOwnerLayerIndex < 0) &&
      (!protectedOwnerLayerIndices || protectedOwnerLayerIndices.length === 0)
    )
  ) {
    return false;
  }
  const offset = pixelIndex * 4;
  const data = sourceImageData.data;
  const heightValue = (
    0.2126 * data[offset] +
    0.7152 * data[offset + 1] +
    0.0722 * data[offset + 2]
  ) | 0;
  const ownerIndex = ownerLayerIndices[heightValue];
  return ownerIndex === protectedOwnerLayerIndex || protectedOwnerLayerIndices?.includes(ownerIndex);
}

export function applySlopeMaterial(
  baseImageData,
  slopeAngles,
  {
    angle = 35,
    comparison = "gte",
    color = "#666c6f",
    sourceImageData = null,
    ownerLayerIndices = null,
    protectedOwnerLayerIndex = null,
    protectedOwnerLayerIndices = null,
  } = {},
) {
  if (!baseImageData || !slopeAngles) return baseImageData;
  const { width, height, data } = baseImageData;
  const output = new ImageData(new Uint8ClampedArray(data), width, height);
  const rgb = hexToRgb(color);

  for (let index = 0; index < slopeAngles.length; index++) {
    if (!slopeMatches(slopeAngles[index], angle, comparison)) continue;
    if (
      slopePixelIsProtected(
        sourceImageData,
        index,
        ownerLayerIndices,
        protectedOwnerLayerIndex,
        protectedOwnerLayerIndices,
      )
    ) continue;
    const offset = index * 4;
    if (output.data[offset + 3] === 0) continue;
    output.data[offset] = rgb.r;
    output.data[offset + 1] = rgb.g;
    output.data[offset + 2] = rgb.b;
    output.data[offset + 3] = 255;
  }
  return output;
}

export function renderSlopeMask(
  sourceImageData,
  slopeAngles,
  {
    angle = 35,
    comparison = "gte",
    color = "#666c6f",
    ownerLayerIndices = null,
    protectedOwnerLayerIndex = null,
    protectedOwnerLayerIndices = null,
  } = {},
) {
  if (!sourceImageData || !slopeAngles) return null;
  const { width, height, data } = sourceImageData;
  const output = new ImageData(width, height);
  const rgb = hexToRgb(color);

  for (let index = 0; index < slopeAngles.length; index++) {
    const offset = index * 4;
    if (data[offset + 3] === 0 || slopeAngles[index] < 0) continue;
    const protectedPixel = slopePixelIsProtected(
      sourceImageData,
      index,
      ownerLayerIndices,
      protectedOwnerLayerIndex,
      protectedOwnerLayerIndices,
    );
    const selected = !protectedPixel && slopeMatches(slopeAngles[index], angle, comparison);
    const shade = Math.round(18 + (Math.min(90, slopeAngles[index]) / 90) * 62);
    output.data[offset] = selected ? rgb.r : protectedPixel ? 49 : shade;
    output.data[offset + 1] = selected ? rgb.g : protectedPixel ? 58 : shade;
    output.data[offset + 2] = selected ? rgb.b : protectedPixel ? 82 : shade;
    output.data[offset + 3] = 255;
  }
  return output;
}

export function computeSlopeCoverage(
  slopeAngles,
  threshold,
  comparison = "gte",
  {
    sourceImageData = null,
    ownerLayerIndices = null,
    protectedOwnerLayerIndex = null,
    protectedOwnerLayerIndices = null,
  } = {},
) {
  if (!slopeAngles?.length) return 0;
  let eligible = 0;
  let selected = 0;
  for (let index = 0; index < slopeAngles.length; index++) {
    if (slopeAngles[index] < 0) continue;
    eligible++;
    if (
      slopeMatches(slopeAngles[index], threshold, comparison) &&
      !slopePixelIsProtected(
        sourceImageData,
        index,
        ownerLayerIndices,
        protectedOwnerLayerIndex,
        protectedOwnerLayerIndices,
      )
    ) {
      selected++;
    }
  }
  return eligible ? (selected / eligible) * 100 : 0;
}

// Wrap an ImageData in a real canvas so it can be composited (drawImage) with
// alpha. Used to overlay Exact-Paint strokes on top of Gradient/Advanced maps.
export function imageDataToCanvas(imageData) {
  const canvas = document.createElement("canvas");
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  canvas.getContext("2d").putImageData(imageData, 0, 0);
  return canvas;
}

// Procedurally generate a grayscale sample height map so users can try the app
// instantly without downloading one. Returns a canvas.
export function generateSampleHeightMap(kind = "hills", size = 512) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const img = ctx.createImageData(size, size);
  const d = img.data;
  const center = size / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let v;
      if (kind === "mountains") {
        const ridged = 1 - Math.abs(fbm(x / 60, y / 60) * 2 - 1);
        v = Math.pow(ridged, 1.3);
      } else if (kind === "island") {
        const base = fbm(x / 85, y / 85);
        const nd = Math.hypot(x - center, y - center) / (size * 0.52);
        v = Math.max(0, Math.min(1, base * 1.25 - nd * 1.15 + 0.12));
      } else {
        // rolling hills
        v = fbm(x / 95, y / 95);
      }
      const g = Math.round(Math.max(0, Math.min(1, v)) * 255);
      const k = (y * size + x) * 4;
      d[k] = g;
      d[k + 1] = g;
      d[k + 2] = g;
      d[k + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function hash2(x, y) {
  let hash = (x * 374761393 + y * 668265263) | 0;
  hash = ((hash ^ (hash >>> 13)) * 1274126177) | 0;
  return ((hash ^ (hash >>> 16)) >>> 0) / 4294967295;
}

function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

function valueNoise(x, y) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = smoothstep(xf);
  const v = smoothstep(yf);
  const a = hash2(xi, yi) * (1 - u) + hash2(xi + 1, yi) * u;
  const b = hash2(xi, yi + 1) * (1 - u) + hash2(xi + 1, yi + 1) * u;
  return a * (1 - v) + b * v;
}

function fbm(x, y) {
  let sum = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let norm = 0;
  for (let index = 0; index < 6; index++) {
    sum += valueNoise(x * frequency, y * frequency) * amplitude;
    norm += amplitude;
    frequency *= 2;
    amplitude *= 0.5;
  }
  return sum / norm; // normalized 0..1
}

function patternValue(x, y, scale, kind, rotation) {
  const cosine = Math.cos(rotation);
  const sine = Math.sin(rotation);
  const rx = (x * cosine - y * sine) / scale;
  const ry = (x * sine + y * cosine) / scale;

  // Every pattern returns a value in 0..1 where LOWER means "material applies".
  // Rounded / organic shapes come from smooth per-pixel distance fields rather
  // than hard-thresholded grid cells, so nothing looks like blocky squares.
  if (kind === "noise") {
    // Richer multi-octave noise for organic, rounded blobs with fine detail.
    return fbm(rx * 0.7, ry * 0.7);
  }
  if (kind === "checker") {
    // Rounded "polka checker": soft circles centred on alternating cells.
    const gx = Math.floor(rx);
    const gy = Math.floor(ry);
    if (((gx + gy) & 1) !== 0) return 1;
    const dx = rx - gx - 0.5;
    const dy = ry - gy - 0.5;
    return Math.min(1, Math.hypot(dx, dy) / 0.5);
  }
  if (kind === "stripes") {
    // Smooth sine bands -> soft rounded stripes instead of hard edges.
    return 0.5 - 0.5 * Math.cos(ry * Math.PI * 2);
  }
  if (kind === "dots") {
    const gx = Math.floor(rx);
    const gy = Math.floor(ry);
    // Jittered centre per cell for organic, perfectly round dots.
    const cx = gx + 0.5 + (hash2(gx, gy) - 0.5) * 0.5;
    const cy = gy + 0.5 + (hash2(gx + 91, gy + 17) - 0.5) * 0.5;
    return Math.min(1, Math.hypot(rx - cx, ry - cy) / 0.5);
  }
  return 0.5;
}

// Evaluate the active pattern for a pixel. Hand-drawn "custom" patterns tile a
// boolean grid (an ON cell means the material applies); everything else uses
// the procedural patterns above.
function evalPattern(combine, x, y) {
  const { scale, kind, rotation } = combine;
  if (kind === "custom" && combine.customCells) {
    const w = combine.customWidth ?? combine.customSize;
    const h = combine.customHeight ?? combine.customSize;
    if (!w || !h) return 0.5;
    const cosine = Math.cos(rotation);
    const sine = Math.sin(rotation);
    const rx = (x * cosine - y * sine) / scale;
    const ry = (x * sine + y * cosine) / scale;
    let gx = Math.floor(rx) % w;
    let gy = Math.floor(ry) % h;
    if (gx < 0) gx += w;
    if (gy < 0) gy += h;
    return combine.customCells[gy * w + gx] ? 0 : 1;
  }
  return patternValue(x, y, scale, kind, rotation);
}

// The result is an index into an existing material list. There is no colour
// interpolation in this path, which keeps every combined pixel on-palette.
// Material zones follow a smooth noise field so their boundaries are organic
// and rounded rather than hard square blocks.
function pickMaterial(x, y, cumulative, scale) {
  const value = fbm(x / (scale * 1.4) + 21.7, y / (scale * 1.4) + 8.3);
  for (let index = 0; index < cumulative.length; index++) {
    if (value <= cumulative[index]) return index;
  }
  return cumulative.length - 1;
}

export function renderWithCombine(sourceImageData, lut, options = {}) {
  const { width, height, data } = sourceImageData;
  const out = new ImageData(width, height);
  const output = out.data;
  const rawCombines = options.combines ?? (options.combine ? [options.combine] : []);
  const combines = rawCombines
    .filter(
      (combine) =>
        combine &&
        Array.isArray(combine.materials) &&
        combine.materials.length >= 2 &&
        combine.min !== undefined &&
        combine.max !== undefined,
    )
    .map((combine) => {
      const materials = combine.materials.map((material) => ({
        rgb: hexToRgb(material.color),
        weight: Math.max(0.001, material.weight ?? 1),
      }));
      const totalWeight = materials.reduce((sum, material) => sum + material.weight, 0) || 1;
      let accumulated = 0;
      const cumulative = materials.map((material) => {
        accumulated += material.weight / totalWeight;
        return accumulated;
      });
      return {
        ...combine,
        materials,
        cumulative,
        scale: Math.max(1, combine.scale ?? 8),
        intensity: Math.max(0, Math.min(1, combine.intensity ?? 0.5)),
        rotation: ((combine.rotation ?? 0) * Math.PI) / 180,
        kind: combine.pattern ?? "noise",
      };
    });

  for (let y = 0, i = 0; y < height; y++) {
    for (let x = 0; x < width; x++, i += 4) {
      const h = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) | 0;
      const base = h * 4;
      let r = lut[base];
      let g = lut[base + 1];
      let b = lut[base + 2];
      let a = lut[base + 3];
      const combine = combines.find(
        (candidate) =>
          h >= candidate.min &&
          h <= candidate.max &&
          (candidate.ownerLayerIndex === undefined ||
            !options.ownerLayerIndices ||
            options.ownerLayerIndices[h] === candidate.ownerLayerIndex),
      );

      if (combine && evalPattern(combine, x, y) < combine.intensity) {
        const material = combine.materials[
          pickMaterial(x, y, combine.cumulative, combine.scale)
        ];
        r = material.rgb.r;
        g = material.rgb.g;
        b = material.rgb.b;
        a = 255;
      }

      output[i] = r;
      output[i + 1] = g;
      output[i + 2] = b;
      output[i + 3] = (a * data[i + 3]) / 255;
    }
  }
  return out;
}
